export type SupportedContentLanguage = Readonly<{
  label: string;
  value: string;
  code: string;
  locale: string;
}>;

export const SUPPORTED_CONTENT_LANGUAGES = [
  { label: 'English', value: 'English', code: 'en', locale: 'en-US' },
  { label: '简体中文', value: 'Chinese', code: 'zh', locale: 'zh-CN' },
  { label: '繁體中文', value: 'Traditional Chinese', code: 'zh', locale: 'zh-TW' },
  { label: 'Español', value: 'Spanish', code: 'es', locale: 'es-ES' },
  { label: 'Français', value: 'French', code: 'fr', locale: 'fr-FR' },
  { label: 'Português', value: 'Portuguese', code: 'pt', locale: 'pt-BR' },
  { label: 'Italiano', value: 'Italian', code: 'it', locale: 'it-IT' },
  { label: '日本語', value: 'Japanese', code: 'ja', locale: 'ja-JP' },
  { label: '한국어', value: 'Korean', code: 'ko', locale: 'ko-KR' },
  { label: 'Deutsch', value: 'German', code: 'de', locale: 'de-DE' },
  { label: 'Русский', value: 'Russian', code: 'ru', locale: 'ru-RU' },
  { label: 'Bahasa Indonesia', value: 'Indonesian', code: 'id', locale: 'id-ID' },
] as const satisfies readonly SupportedContentLanguage[];

const normalizeLanguageLookup = (value: unknown): string =>
  String(value || '').trim().toLowerCase().replaceAll('_', '-');

const LANGUAGE_ALIASES = new Map<string, SupportedContentLanguage>();

for (const language of SUPPORTED_CONTENT_LANGUAGES) {
  for (const alias of [language.label, language.value, language.locale]) {
    LANGUAGE_ALIASES.set(normalizeLanguageLookup(alias), language);
  }

  // A bare language code resolves to the first configured variant. For zh,
  // that intentionally means Simplified Chinese; zh-TW remains explicit.
  if (!LANGUAGE_ALIASES.has(language.code)) {
    LANGUAGE_ALIASES.set(language.code, language);
  }
}

LANGUAGE_ALIASES.set('simplified chinese', SUPPORTED_CONTENT_LANGUAGES[1]);
LANGUAGE_ALIASES.set('chinese (simplified)', SUPPORTED_CONTENT_LANGUAGES[1]);
LANGUAGE_ALIASES.set('chinese (traditional)', SUPPORTED_CONTENT_LANGUAGES[2]);

export const findSupportedContentLanguage = (
  value: unknown,
): SupportedContentLanguage | undefined => LANGUAGE_ALIASES.get(normalizeLanguageLookup(value));

export const isSupportedContentLanguage = (value: unknown): boolean =>
  findSupportedContentLanguage(value) !== undefined;

export const getSupportedContentLanguageCode = (value: unknown): string | undefined =>
  findSupportedContentLanguage(value)?.code;

